import { forwardRef, ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import cn from "clsx";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "warning";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Zeigt Spinner + deaktiviert den Button. */
  loading?: boolean;
  /** Optionales Icon links vom Label. */
  icon?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-accent-500 hover:bg-accent-600 text-white",
  secondary: "bg-surface border border-border hover:bg-surface-3 text-text",
  ghost: "bg-transparent hover:bg-surface-3 text-text-2",
  danger: "bg-danger hover:bg-danger/90 text-white",
  // Für Knöpfe, die AUF einer warning-getönten Fläche sitzen (Entscheidungs-
  // und Lücken-Zeilen im Fragebogen-Review). Der Text ist `text-text` und
  // bewusst NICHT der Warnton: Amber auf Amber war der Kontrast, der in der
  // Abnahme angemerkt wurde. `text-text` ist im Hellmodus fast schwarz, im
  // Dunkelmodus fast weiß — die Lesbarkeit hängt damit am Theme und nicht an
  // der Fläche, auf der der Knopf gerade liegt.
  warning: "bg-warning-soft border border-warning/50 hover:bg-warning-soft/70 text-text",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1 text-xs gap-1.5",
  md: "px-3.5 py-1.5 text-sm gap-2",
  lg: "px-4 py-2 text-sm gap-2",
};

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: "w-3.5 h-3.5",
  md: "w-4 h-4",
  lg: "w-4 h-4",
};

/** Einziger erlaubter Primary-Button-Stil (Spec §3). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, icon, disabled, className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium",
        "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className={cn(ICON_SIZE[size], "animate-spin")} />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});

export default Button;
